import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface MyListing {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'under_review';
  install_count: number;
  rating_avg: number;
  rating_count: number;
  created_at: string;
}

const statusBadgeClasses: Record<string, string> = {
  published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  under_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

const statusLabels: Record<string, string> = {
  published: 'Published',
  draft: 'Draft',
  under_review: 'Under Review',
};

export function MyListings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['marketplace-my-listings'],
    queryFn: () => api<{ listings: MyListing[] }>('marketplace/my-listings'),
  });

  const unpublishMutation = useMutation({
    mutationFn: (listingId: string) =>
      api(`marketplace/listings/${listingId}/unpublish`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-my-listings'] });
    },
  });

  const listings = data?.listings ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">My Listings</h1>
          <p className="text-muted-foreground">Manage your published marketplace solutions.</p>
        </div>
        <button
          onClick={() => navigate('/marketplace/publish')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex-shrink-0"
        >
          Publish New
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-lg border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-3">Failed to load your listings.</p>
          <button
            onClick={() => refetch()}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      ) : listings.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          <p className="mb-4">You have not published any solutions yet.</p>
          <button
            onClick={() => navigate('/marketplace/publish')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
          >
            Publish Your First Solution
          </button>
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          {/* Table header */}
          <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Name</span>
            <span className="w-28 text-center">Status</span>
            <span className="w-20 text-right">Installs</span>
            <span className="w-20 text-right">Rating</span>
            <span className="w-28 text-right">Created</span>
            <span className="w-40 text-right">Actions</span>
          </div>

          {/* Rows */}
          {listings.map((listing) => (
            <div
              key={listing.id}
              className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50 transition-colors"
            >
              <span className="flex-1 text-sm font-medium text-foreground truncate">
                {listing.name}
              </span>
              <span className="w-28 flex md:justify-center">
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClasses[listing.status] ?? ''}`}>
                  {statusLabels[listing.status] ?? listing.status}
                </span>
              </span>
              <span className="w-20 text-sm text-muted-foreground text-right">
                {listing.install_count.toLocaleString()}
              </span>
              <span className="w-20 text-sm text-muted-foreground text-right">
                {listing.rating_count > 0
                  ? `${listing.rating_avg.toFixed(1)} (${listing.rating_count})`
                  : '--'}
              </span>
              <span className="w-28 text-xs text-muted-foreground text-right">
                {new Date(listing.created_at).toLocaleDateString()}
              </span>
              <div className="w-40 flex items-center justify-end gap-2">
                <button
                  onClick={() => navigate(`/marketplace/${listing.id}`)}
                  className="text-xs px-2 py-1 rounded border hover:bg-accent transition-colors"
                >
                  View
                </button>
                <button
                  onClick={() => navigate(`/marketplace/publish?edit=${listing.id}`)}
                  className="text-xs px-2 py-1 rounded border hover:bg-accent transition-colors"
                >
                  Edit
                </button>
                {listing.status === 'published' && (
                  <button
                    onClick={() => unpublishMutation.mutate(listing.id)}
                    disabled={unpublishMutation.isPending}
                    className="text-xs px-2 py-1 rounded border text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
